import { useState, useEffect, useCallback } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Search, X, MessageCircle } from 'lucide-react'

const CACHE_KEY = 'fm_guests_cache'

function guestDisplayName(g) { return g.plus_one ? `Mr & Mrs ${g.name}` : g.name }

function buildMsg(guest, wedding, events, event) {
  const name   = guestDisplayName(guest)
  const couple = `${wedding.partner1_name} & ${wedding.partner2_name}`
  const site   = `${window.location.origin}/w/${wedding.slug}`
  const link   = `${window.location.origin}/w/${wedding.slug}/invite/${guest.invite_token}`
  const ids    = guest.invited_event_ids
  const guestEvents = (!ids||ids.length===0) ? events : events.filter(e=>ids.includes(e.id))
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : ''
  const evLines = guestEvents.map(e=>{
    let block = `• ${e.type}${e.date?` on ${formatDate(e.date)}`:''}${e.venue?` at ${e.venue}`:''}`
    const parts = [e.dress_code, e.dress_code_fabric].filter(Boolean)
    if (parts.length || e.dress_code_notes) {
      let dc = `\n   Dress code: ${parts.join(' · ')}`
      if (e.dress_code_notes) dc += parts.length ? ` — ${e.dress_code_notes}` : e.dress_code_notes
      block += dc
    }
    return block
  }).join('\n\n')
  return `Dear ${name},\n\nYou're warmly invited to the wedding of ${couple}\n\n${evLines}\n\nRSVP: ${link}\n\nWedding details: ${site}\n\nWith love,\n${couple}`
}

function AddGuestModal({ weddingId, events, defaultEventId, onClose, onAdded, isPremium }) {
  const [name, setName]     = useState('')
  const [phone, setPhone]   = useState('')
  const [plusOne, setPlusOne] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState(defaultEventId ? [defaultEventId] : [])
  const [allEvs, setAllEvs] = useState(!defaultEventId)
  const [saving, setSaving] = useState(false)
  const [err, setErr]       = useState('')

  function toggleEv(id) { setSelectedEvents(prev=>prev.includes(id)?prev.filter(e=>e!==id):[...prev,id]) }

  async function save() {
    if (!name.trim()) { setErr('Please enter a name.'); return }
    if (!phone.trim()) { setErr('Please enter a phone number.'); return }
    setSaving(true); setErr('')
    try {
      // Free plan: max 5 guests
      if (!isPremium) {
        const { count } = await supabase.from('guests').select('*', {count:'exact',head:true}).eq('wedding_id',weddingId)
        if ((count||0) >= 5) { setErr("You've hit the free plan limit — upgrade to add unlimited guests."); setSaving(false); return }
      }
      const { error } = await supabase.from('guests').insert({
        wedding_id: weddingId, name: name.trim(), plus_one: plusOne,
        phone: phone.trim(),
        guest_type: 'general', rsvp_status: 'pending',
        invited_event_ids: allEvs ? [] : selectedEvents,
      })
      if (error) { setErr(error.message); setSaving(false); return }
      await onAdded()
      onClose()
    } catch (e) {
      setErr(e?.message || 'Could not add the guest — please try again.')
      setSaving(false)
    }
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)'}}>
      <div className="card fade-up" style={{width:'100%',maxWidth:380,padding:'1.75rem',position:'relative',maxHeight:'90vh',overflowY:'auto'}}>
        <button onClick={onClose} style={{position:'absolute',top:12,right:12,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC'}}><X size={17}/></button>
        <h3 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.05rem',margin:'0 0 1.125rem'}}>Add Guest</h3>
        <div style={{display:'flex',flexDirection:'column',gap:'0.875rem'}}>
          <div><label className="label">Full Name *</label><input className="input-field" placeholder="Guest name" value={name} onChange={e=>setName(e.target.value)} autoFocus/></div>
          <div><label className="label">Phone *</label><input className="input-field" placeholder="08012345678" value={phone} onChange={e=>setPhone(e.target.value)}/></div>
          <div>
            <label className="label">Invited to</label>
            <div style={{display:'flex',flexDirection:'column',gap:'0.35rem'}}>
              <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',padding:'0.5rem 0.75rem',background:allEvs?'rgba(155,175,155,0.1)':'#FAFAF8',borderRadius:'0.5rem',border:`1.5px solid ${allEvs?'rgba(155,175,155,0.4)':'#E5E0D8'}`}}>
                <input type="checkbox" checked={allEvs} onChange={e=>{setAllEvs(e.target.checked);if(e.target.checked)setSelectedEvents([])}} style={{width:14,height:14,accentColor:'#9BAF9B'}}/>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:600,color:'#1A1A1A'}}>All Events</span>
              </label>
              {events.map(ev=>(
                <label key={ev.id} style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',padding:'0.5rem 0.75rem',background:selectedEvents.includes(ev.id)?'rgba(196,130,106,0.06)':'#FAFAF8',borderRadius:'0.5rem',border:`1.5px solid ${selectedEvents.includes(ev.id)?'rgba(196,130,106,0.3)':'#E5E0D8'}`}}>
                  <input type="checkbox" checked={selectedEvents.includes(ev.id)} onChange={()=>toggleEv(ev.id)} style={{width:14,height:14,accentColor:'#C4826A'}}/>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#1A1A1A'}}>{ev.type}</span>
                </label>
              ))}
            </div>
          </div>
          <label style={{display:'flex',alignItems:'flex-start',gap:'0.5rem',cursor:'pointer',padding:'0.625rem',background:'#FFFFFF',borderRadius:'0.75rem',border:'1px solid #E5E0D8'}}>
            <input type="checkbox" checked={plusOne} onChange={e=>setPlusOne(e.target.checked)} style={{width:14,height:14,accentColor:'#C4826A',marginTop:2}}/>
            <div>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.82rem',margin:'0 0 0.1rem'}}>Coming with a +1 (spouse)</p>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#6B6B6B',margin:0}}>Counts as 2 people</p>
            </div>
          </label>
        </div>
        {err&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#C4826A',marginTop:'0.625rem'}}>{err}</p>}
        <div style={{display:'flex',gap:'0.625rem',marginTop:'1.25rem'}}>
          <button className="btn-secondary" onClick={onClose} style={{flex:1}}>Cancel</button>
          <button onClick={save} disabled={saving||(!allEvs&&selectedEvents.length===0)}
            style={{flex:1,background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.7rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.82rem',opacity:(saving||(!allEvs&&selectedEvents.length===0))?0.5:1}}>
            {saving?'Adding…':'Add Guest'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function GuestsTab({ event }) {
  const { wedding, events , isPremium } = useWedding()

  const navigate            = useNavigate()
  const [guests, setGuests]   = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAdd, setShowAdd] = useState(false)
  const [sending, setSending] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const fetchGuests = useCallback(async () => {
    if (!event?.id || !wedding?.id) return
    setLoading(true)
    // Fetch ALL wedding guests and filter client-side for this event
    const { data } = await supabase.from('guests').select('*')
      .eq('wedding_id', wedding.id).order('created_at',{ascending:false})
    // Show guests invited to this event or all events
    const evGuests = (data||[]).filter(g => {
      const ids = g.invited_event_ids
      if (!ids || ids.length === 0) return true // all events
      return ids.includes(event.id) || g.event_id === event.id
    })
    setGuests(evGuests); setLoading(false)
    // Update cache
    try { localStorage.setItem(`${CACHE_KEY}_${wedding.id}`, JSON.stringify(data||[])) } catch {}
  },[event?.id, wedding?.id])

  useEffect(()=>{ fetchGuests() },[fetchGuests])

  const FREE_GUEST_LIMIT = 5
  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>One moment, pulling up your plans…</p>
    </div>
  )

  async function deleteGuest(id) {
    setDeleting(id)
    await supabase.from('guests').delete().eq('id',id)
    setGuests(prev=>prev.filter(g=>g.id!==id))
    setDeleting(null)
  }

  async function sendWhatsApp(guest) {
    setSending(guest.id)
    const msg = buildMsg(guest, wedding, events, event)
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,'_blank')
    const updates = { whatsapp_sent:true, whatsapp_sent_at:new Date().toISOString() }
    await supabase.from('guests').update(updates).eq('id',guest.id)
    setGuests(prev=>prev.map(g=>g.id===guest.id?{...g,...updates}:g))
    setSending(null)
  }

  // Stats
  const confirmed = guests.filter(g=>g.rsvp_status==='attending'||g.rsvp_status==='confirmed')
  const pending   = guests.filter(g=>g.rsvp_status==='pending')
  const declined  = guests.filter(g=>g.rsvp_status==='declined'||g.rsvp_status==='rejected')
  const total     = guests.reduce((s,g)=>s+(g.plus_one?2:1),0)

  const filtered = guests.filter(g => {
    const ms = !search||g.name.toLowerCase().includes(search.toLowerCase())
    const mf = statusFilter==='all'?true:statusFilter==='confirmed'?(g.rsvp_status==='attending'||g.rsvp_status==='confirmed'):statusFilter==='declined'?(g.rsvp_status==='declined'||g.rsvp_status==='rejected'):g.rsvp_status==='pending'
    return ms&&mf
  })



  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
      <div>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', margin:'0 0 0.2rem' }}>Guests &amp; RSVP</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>Invite people to <strong>{event?.type}</strong> and track their replies.</p>
      </div>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'0.75rem'}}>
        <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:0}}>{total} people · {confirmed.reduce((s,g)=>s+(g.plus_one?2:1),0)} confirmed</p>
        <button onClick={()=>setShowAdd(true)} style={{display:'flex',alignItems:'center',gap:'0.4rem',background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.5rem 1rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.8rem'}}>
          <UserPlus size={14}/> Add Guest
        </button>
      </div>

      {/* Stats — 4 metrics */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'0.4rem'}}>
        {[
          {label:'Total',value:total},
          {label:'Confirmed',value:confirmed.reduce((s,g)=>s+(g.plus_one?2:1),0)},
          {label:'Pending',value:pending.reduce((s,g)=>s+(g.plus_one?2:1),0)},
          {label:'Declined',value:declined.reduce((s,g)=>s+(g.plus_one?2:1),0)},
        ].map(s=>(
          <div key={s.label} style={{background:'white',border:'1px solid #E5E0D8',borderRadius:'0.75rem',padding:'0.5rem',textAlign:'center'}}>
            <p style={{fontFamily:'Gibs, serif',fontSize:'1.2rem',color:'#1A1A1A',margin:'0 0 0.1rem',fontWeight:400}}>{s.value}</p>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.6rem',color:'#6B6B6B',margin:0,fontWeight:600}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:'0 0 45%',minWidth:120}}>
          <Search size={12} color="#BCBCBC" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)'}}/>
          <input className="input-field" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} style={{paddingLeft:'1.875rem',fontSize:'0.8rem'}}/>
        </div>
        <div style={{display:'flex',background:'white',borderRadius:100,padding:'2px',gap:'1px',flexShrink:0}}>
          {[{v:'all',l:'All'},{v:'confirmed',l:'Confirmed'},{v:'pending',l:'Pending'},{v:'declined',l:'Declined'}].map(t=>(
            <button key={t.v} onClick={()=>setStatusFilter(t.v)} style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',fontWeight:statusFilter===t.v?700:500,padding:'0.25rem 0.625rem',borderRadius:100,border:'none',cursor:'pointer',background:statusFilter===t.v?'white':'transparent',color:statusFilter===t.v?'#1A1A1A':'#6B6B6B',transition:'all 0.15s'}}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading?(
        <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',padding:'1rem',fontSize:'0.78rem'}}>Just a second…</p>
      ):filtered.length===0?(
        <div style={{textAlign:'center',padding:'2rem'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#BCBCBC'}}>{guests.length===0?'No one on the list for this event yet.':'No results.'}</p>
        </div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:'0.35rem'}}>
          {filtered.map(guest=>{
            const status = guest.rsvp_status
            const sc = status==='attending'||status==='confirmed'?{label:'Confirmed',color:'#9BAF9B',bg:'rgba(155,175,155,0.1)'}:status==='declined'||status==='rejected'?{label:'Declined',color:'#C4826A',bg:'rgba(196,130,106,0.08)'}:{label:'Pending',color:'#BCBCBC',bg:'rgba(107,107,107,0.06)'}
            return (
              <div key={guest.id} style={{background:'white',border:'1px solid #E5E0D8',borderRadius:'0.75rem',padding:'0.625rem 0.875rem',display:'flex',alignItems:'center',gap:'0.5rem',flexWrap:'wrap'}}>
                <div style={{width:30,height:30,background:'white',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{fontFamily:'Gibs, serif',fontSize:'0.8rem',color:'#C4826A'}}>{guest.name[0].toUpperCase()}</span>
                </div>
                <div style={{flex:1,minWidth:80}}>
                  <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#1A1A1A',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {guestDisplayName(guest)}{guest.plus_one&&<span style={{fontSize:'0.6rem',color:'#9BAF9B',marginLeft:4,fontWeight:600}}>+1</span>}
                  </p>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'0.25rem',background:sc.bg,borderRadius:100,padding:'0.15rem 0.5rem',flexShrink:0}}>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.63rem',fontWeight:700,color:sc.color}}>{sc.label}</span>
                </div>
                <button onClick={()=>sendWhatsApp(guest)} disabled={sending===guest.id} style={{display:'flex',alignItems:'center',gap:'0.25rem',background:guest.whatsapp_sent?'rgba(155,175,155,0.08)':'rgba(196,130,106,0.07)',border:`1px solid ${guest.whatsapp_sent?'rgba(155,175,155,0.25)':'rgba(196,130,106,0.2)'}`,borderRadius:100,padding:'0.2rem 0.5rem',cursor:'pointer',flexShrink:0}}>
                  <MessageCircle size={10} color={guest.whatsapp_sent?'#9BAF9B':'#C4826A'}/>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',fontWeight:600,color:guest.whatsapp_sent?'#9BAF9B':'#C4826A',whiteSpace:'nowrap'}}>{sending===guest.id?'…':guest.whatsapp_sent?'Resend':'Send'}</span>
                </button>
                <button onClick={()=>deleteGuest(guest.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#BCBCBC',padding:'3px',flexShrink:0}}>
                  {deleting===guest.id?'…':<X size={12}/>}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {showAdd&&<AddGuestModal weddingId={wedding.id} events={events} defaultEventId={event.id} isPremium={isPremium} onClose={()=>setShowAdd(false)} onAdded={fetchGuests}/>}
    </div>
  )
}
